<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="1000px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Zgłoś zapotrzebowanie materiału
                </h1>
            </v-card-title>

            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="items"
                    :mobile-breakpoint="0"
                    :items-per-page="-1"
                    item-key="uid"
                    hide-default-footer
                    no-data-text="Brak pozycji"
                >
                    <template #item.materialTypeId="{item}">
                        <v-select
                            v-model="item.materialTypeId"
                            :items="materialTypes"
                            item-text="name"
                            item-value="id"
                            label="typ materiału"
                            dense
                            outlined
                            :disabled="loadingMaterialTypes || isSending"
                            hide-details="auto"
                            @change="onMaterialTypeChange(item)"
                        />
                    </template>

                    <template #item.materialId="{item}">
                        <div class="d-flex justify-center" style="max-width: 260px;">
                            <v-select
                                v-model="item.materialId"
                                :items="materialsForType(item.materialTypeId)"
                                item-text="name"
                                item-value="id"
                                label="materiał"
                                dense
                                outlined
                                :disabled="!item.materialTypeId || isMaterialsLoading(item.materialTypeId) || isSending"
                                :loading="isMaterialsLoading(item.materialTypeId)"
                                hide-details="auto"
                                @change="onMaterialChange(item)"
                            />
                        </div>
                    </template>

                    <template #item.amount="{item}">
                        <div class="d-flex justify-center">
                            <v-text-field
                                v-model="item.amount"
                                type="number"
                                min="0"
                                label="ilość"
                                dense
                                outlined
                                :disabled="isSending"
                                hide-details="auto"
                            />
                        </div>
                    </template>

                    <template #item.unit="{item}">
                        <div class="text-center">
                            {{ unitOfMeasureName(item) }}
                        </div>
                    </template>

                    <template #item.actions="{item}">
                        <v-btn
                            icon
                            color="error"
                            data-sentry-label="Usuń"
                            :disabled="isSending"
                            @click="removeRow(item)"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </template>

                    <template #footer>
                        <div class="pa-3">
                            <v-btn
                                color="secondary"
                                small
                                data-sentry-label="Dodaj kolejny materiał"
                                :disabled="isSending"
                                @click="addRow"
                            >
                                <v-icon left small>
                                    mdi-plus
                                </v-icon>
                                Dodaj kolejny materiał
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn color="error" text data-sentry-label="Anuluj" :disabled="isSending" @click="onClose">
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Wyślij"
                    :loading="isSending"
                    :disabled="!canSubmit"
                    @click="onSubmit"
                >
                    Wyślij
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ReportMaterialReservationDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        objectNumber: {
            type: String,
            default: ''
        },
        productionSiteId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            headers: [
                {
                    text: 'Typ materiału',
                    value: 'materialTypeId',
                    sortable: false,
                    width: 260
                },
                {
                    text: 'Materiał',
                    value: 'materialId',
                    sortable: false,
                    align: 'center',
                    width: 260
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    sortable: false,
                    align: 'center',
                    width: 110
                },
                {
                    text: 'JM',
                    value: 'unit',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 80
                }
            ],
            items: [],
            materialTypes: [],
            materialsByTypeId: {},
            materialsLoadingByTypeId: {},
            loadingMaterialTypes: false,
            isSending: false,
            rowCounter: 0
        }
    },
    computed: {
        canSubmit() {
            if (this.isSending || this.loadingMaterialTypes) return false
            if (!this.objectNumber) return false
            if (!this.productionSiteId && this.productionSiteId !== 0) return false
            if (!this.items.length) return false

            return this.items.every(item => {
                return !!item.materialTypeId && !!item.materialId && Number(item.amount) > 0
            })
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.initialize()
            }
        }
    },
    methods: {
        initialize() {
            this.items = []
            this.materialsByTypeId = {}
            this.materialsLoadingByTypeId = {}
            this.addRow()
            this.fetchMaterialTypes()
        },
        addRow() {
            this.rowCounter += 1

            this.items.push({
                uid: `${Date.now()}-${this.rowCounter}`,
                materialTypeId: null,
                materialId: null,
                amount: null
            })
        },
        removeRow(item) {
            const index = this.items.indexOf(item)

            if (index !== -1) {
                this.items.splice(index, 1)
            }

            if (this.items.length === 0) {
                this.addRow()
            }
        },
        onClose() {
            this.$emit('on-close')
        },
        async fetchMaterialTypes() {
            this.loadingMaterialTypes = true

            try {
                const response = await this.$store.dispatch('reports/getReportMaterialTypes')

                this.materialTypes = response?.data?.results || response?.data || []
            } catch (error) {
                errorHandler(error)
            } finally {
                this.loadingMaterialTypes = false
            }
        },
        materialsForType(materialTypeId) {
            if (!materialTypeId) return []

            return this.materialsByTypeId[materialTypeId] || []
        },
        isMaterialsLoading(materialTypeId) {
            if (!materialTypeId) return false

            return this.materialsLoadingByTypeId[materialTypeId] === true
        },
        async fetchMaterialsForType(materialTypeId) {
            if (!materialTypeId) return
            if (this.materialsByTypeId[materialTypeId]) return
            if (this.isMaterialsLoading(materialTypeId)) return

            this.$set(this.materialsLoadingByTypeId, materialTypeId, true)

            try {
                const response = await this.$store.dispatch('reports/getReportMaterials', { materialTypeId })
                const materials = response?.data?.results || response?.data || []

                this.$set(this.materialsByTypeId, materialTypeId, materials)
            } catch (error) {
                errorHandler(error)
            } finally {
                this.$set(this.materialsLoadingByTypeId, materialTypeId, false)
            }
        },
        onMaterialTypeChange(row) {
            row.materialId = null
            row.amount = null

            if (row.materialTypeId) {
                this.fetchMaterialsForType(row.materialTypeId)
            }
        },
        onMaterialChange(row) {
            row.amount = null
        },
        selectedMaterial(row) {
            const materials = this.materialsForType(row.materialTypeId)

            const selectedId = row?.materialId

            if (selectedId === null || typeof selectedId === 'undefined') return null

            return materials.find(m => String(m.id) === String(selectedId)) || null
        },
        unitOfMeasureName(row) {
            const material = this.selectedMaterial(row)

            if (!material) return ''

            const uom = material.unitOfMeasure || material.unit_of_measure || material.unit || material.uom || null

            if (typeof uom === 'string') return uom

            return uom?.name || uom?.symbol || material.unitOfMeasureName || material.unitName || ''
        },
        async onSubmit() {
            if (!this.canSubmit) {
                return
            }

            this.isSending = true

            const reportMaterialReservations = this.items.map(item => ({
                amount: Number(item.amount),
                materialId: item.materialId,
                objectNumber: String(this.objectNumber),
                productionSiteId: this.productionSiteId
            }))

            const payload = {
                reportMaterialReservations
            }

            try {
                await this.$store.dispatch('reports/createReportMaterialReservation', payload)
                this.onClose()
            } catch (error) {
                errorHandler(error)
            } finally {
                this.isSending = false
            }
        }
    }
}
</script>
